import {
  AreaChartOutlined,
  LineOutlined,
  EditOutlined,
  AimOutlined,
  RiseOutlined,
  DotChartOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import {
  LengthTool,
  AngleTool,
  PlanarFreehandROITool,
} from '@cornerstonejs/tools';
import { SIMPSON_TOOL_NAME } from '@/utils/measurementToolMapping';
import {
  SPECTRAL_DOPPLER_TOOL_NAMES,
  type SpectralDopplerAvailability,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import { spectralDopplerUnavailableMessage } from '@/features/study-detail/Viewer/tools/SpectralDoppler/useSpectralDopplerAvailability';
import styles from './MeasurementPopover.module.css';

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.userAgent);
const modKey = isMac ? '⌥' : 'Alt';

interface MeasurementPopoverProps {
  open: boolean;
  onClose: () => void;
  activeTool?: string;
  onToolChange?: (tool: string) => void;
  spectralDopplerAvailability?: SpectralDopplerAvailability;
}

export function MeasurementPopover({
  open,
  onClose,
  activeTool,
  onToolChange,
  spectralDopplerAvailability,
}: MeasurementPopoverProps) {
  const dopplerAvailable = spectralDopplerAvailability?.available ?? false;
  const dopplerTooltip = dopplerAvailable
    ? undefined
    : spectralDopplerUnavailableMessage(
        spectralDopplerAvailability?.reason ?? 'no-region'
      );
  // 2D tools don't take a PW/CW strip, calibrated or not.
  const twoDDisabled = spectralDopplerAvailability?.spectralRegion ?? false;
  const twoDTooltip = twoDDisabled ? 'Select a 2D image to measure' : undefined;

  const pick = (toolName: string) => {
    onToolChange?.(toolName);
    onClose();
  };

  return (
    <div className={styles.popover} data-open={open}>
      <div className={styles.category}>
        <span className={styles.categoryLabel}>Distance</span>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === LengthTool.toolName}
          disabled={twoDDisabled}
          title={twoDTooltip}
          onClick={() => pick(LengthTool.toolName)}
        >
          <LineOutlined />
          <span>Length</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>L</kbd>
          </span>
        </button>
      </div>

      <div className={styles.categoryDivider} />

      <div className={styles.category}>
        <span className={styles.categoryLabel}>Angles</span>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === AngleTool.toolName}
          disabled={twoDDisabled}
          title={twoDTooltip}
          onClick={() => pick(AngleTool.toolName)}
        >
          <span className={styles.angleGlyph}>∠</span>
          <span>Angle</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>A</kbd>
          </span>
        </button>
      </div>

      <div className={styles.categoryDivider} />

      <div className={styles.category}>
        <span className={styles.categoryLabel}>Areas</span>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === PlanarFreehandROITool.toolName}
          disabled={twoDDisabled}
          title={twoDTooltip}
          onClick={() => pick(PlanarFreehandROITool.toolName)}
        >
          <EditOutlined />
          <span>Freehand ROI</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>F</kbd>
          </span>
        </button>
      </div>

      <div className={styles.categoryDivider} />

      <div className={styles.category}>
        <span className={styles.categoryLabel}>Volumetric</span>
        {/* Without an armed volume row the trace measures but fills no row. */}
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === SIMPSON_TOOL_NAME}
          disabled={twoDDisabled}
          title={twoDTooltip}
          onClick={() => pick(SIMPSON_TOOL_NAME)}
        >
          <AreaChartOutlined />
          <span>Simpson</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>V</kbd>
          </span>
        </button>
      </div>

      <div className={styles.categoryDivider} />

      <div className={styles.category}>
        <span className={styles.categoryLabel}>Doppler</span>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === SPECTRAL_DOPPLER_TOOL_NAMES.PROBE}
          disabled={!dopplerAvailable}
          title={dopplerTooltip}
          onClick={() => pick(SPECTRAL_DOPPLER_TOOL_NAMES.PROBE)}
        >
          <AimOutlined />
          <span>Velocity</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>D</kbd>
          </span>
        </button>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT}
          disabled={!dopplerAvailable}
          title={dopplerTooltip}
          onClick={() => pick(SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT)}
        >
          <RiseOutlined />
          <span>Slope</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>S</kbd>
          </span>
        </button>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === SPECTRAL_DOPPLER_TOOL_NAMES.VTI}
          disabled={!dopplerAvailable}
          title={dopplerTooltip}
          onClick={() => pick(SPECTRAL_DOPPLER_TOOL_NAMES.VTI)}
        >
          <DotChartOutlined />
          <span>VTI Trace</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>T</kbd>
          </span>
        </button>
        <button
          type="button"
          className={styles.menuItem}
          data-active={activeTool === SPECTRAL_DOPPLER_TOOL_NAMES.TIME}
          disabled={!dopplerAvailable}
          title={dopplerTooltip}
          onClick={() => pick(SPECTRAL_DOPPLER_TOOL_NAMES.TIME)}
        >
          <FieldTimeOutlined />
          <span>Time</span>
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>{modKey}</kbd>
            <kbd className={styles.kbd}>I</kbd>
          </span>
        </button>
      </div>
    </div>
  );
}
